import type {IAccount} from '../../domain/finnova/account';
import type {IAddress} from '../../domain/finnova/address';
import type {IFinnovaCode} from '../../domain/finnova/code';
import type {ICommunicationEntries} from '../../domain/finnova/communication-entry';
import type {ICrmKundenTopf} from '../../domain/finnova/crm-kundentopf';
import type {IEbankingVT} from '../../domain/finnova/ebanking-vt';
import type {IKarte} from '../../domain/finnova/karte';
import type {IKontodepot} from '../../domain/finnova/kontodepot';
import type {IMeldung} from '../../domain/finnova/meldung';
import type {
    IFetchFinnovaCodesRequest,
    IFetchFinnovaDebitkartenRequest,
    IFetchFinnovaKreditkartenRequest,
    IFetchFinnovaRequest,
} from '../../domain/finnova/requests';
import type {IPowerOfAttorney} from '../../domain/finnova';
import type {IKreditkarte} from '../../domain/finnova/kreditkarte';
import type {IDebitkarte} from '../../domain';
import {ApiBaseService} from '../api-base-service';

export const Endpoint = '/forward';

export enum BSAEndpoint {
    ACCOUNTS = 'accounts',
    ADDRESSES = 'addresses',
    CODE_TABLE = 'einerTabellen',
    COMMUNICATION_ENTRIES = 'communicationentries',
    CRM_KUNDENTOEPFE = 'crmKundenToepfe',
    DEBITKARTEN = 'debitkarten',
    EBANKING_VT = 'ebankingVt',
    KARTEN = 'karten',
    KARTEN_LIMIT = 'karten/:cardLnr/limit/:cardLimLnr',
    KONTO_DEPOT = 'kontodepot',
    KREDITKARTEN = 'kreditkarten',
    KUNDEN_DETAILS = 'kunden/:kdlnr',
    MELDUNGEN = 'meldungen',
    VOLLMACHTEN = 'vollmachten',
}

export class FinnovaApiService extends ApiBaseService {
    constructor(context = '/delegate/customer-base/v1') {
        super(context);
    }

    async getCodes(request: IFetchFinnovaCodesRequest): Promise<IFinnovaCode[]> {
        return this.http
            .post<IFinnovaCode[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.CODE_TABLE,
                ...request,
            })
            .then(({data}) => data);
    }

    async getAddresses(request: IFetchFinnovaRequest): Promise<IAddress[]> {
        return this.http
            .post<IAddress[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.ADDRESSES,
                ...request,
            })
            .then(({data}) => data);
    }

    async getCommunicationEntries(request: IFetchFinnovaRequest): Promise<ICommunicationEntries> {
        return this.http
            .post<ICommunicationEntries>(Endpoint, {
                bsaEndpoint: BSAEndpoint.COMMUNICATION_ENTRIES,
                ...request,
            })
            .then(({data}) => data);
    }

    async getDebitkarte(request: IFetchFinnovaDebitkartenRequest): Promise<IDebitkarte> {
        const {data} = await this.http.post<IDebitkarte>(Endpoint, {
            bsaEndpoint: BSAEndpoint.DEBITKARTEN,
            ...request,
        });

        if (Array.isArray(data)) {
            return data[0];
        }

        return data;
    }

    async getKontodepots(request: IFetchFinnovaRequest): Promise<IKontodepot[]> {
        const {data} = await this.http.post<IKontodepot[]>(Endpoint, {
            bsaEndpoint: BSAEndpoint.KONTO_DEPOT,
            ...request,
        });

        const kontoDepots = data.reduce((map, kontoDepot) => {
            if (!map.has(kontoDepot.ktLnr)) {
                map.set(kontoDepot.ktLnr, kontoDepot);
            }

            return map;
        }, new Map<number, IKontodepot>());

        return Array.from(kontoDepots.values());
    }

    async getKarten(request: IFetchFinnovaRequest): Promise<IKarte[]> {
        const {data} = await this.http.post<IKarte[]>(Endpoint, {
            bsaEndpoint: BSAEndpoint.KARTEN,
            ...request,
        });

        const cards = data.reduce((map, card) => {
            const existingCard = map.get(card.cardLnr);
            if (existingCard) {
                map.set(card.cardLnr, {
                    ...existingCard,
                    limiten: {
                        ...existingCard.limiten,
                        ...card.limiten,
                    },
                });
            } else {
                map.set(card.cardLnr, card);
            }

            return map;
        }, new Map<number, IKarte>());

        return Array.from(cards.values());
    }

    async getKreditkarte(request: IFetchFinnovaKreditkartenRequest): Promise<IKreditkarte> {
        const {data} = await this.http.post<IKreditkarte>(Endpoint, {
            bsaEndpoint: BSAEndpoint.KREDITKARTEN,
            ...request,
        });

        if (Array.isArray(data)) {
            return data[0];
        }

        return data;
    }

    async getMeldungen(request: IFetchFinnovaRequest): Promise<IMeldung[]> {
        return this.http
            .post<IMeldung[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.MELDUNGEN,
                ...request,
            })
            .then(({data}) => data);
    }

    async getCrmKundenToepfe(request: IFetchFinnovaRequest): Promise<ICrmKundenTopf[]> {
        const response = await this.http
            .post<ICrmKundenTopf[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.CRM_KUNDENTOEPFE,
                ...request,
            })
            .then(({data}) => data);

        // this is a workaround for the missing artCode in the response, for finnova 0 and null is the same,
        // this leads to errors in the frontend, so we need to set the artCode to 0 if it is null
        return response.map((crmKundenTopf) => {
            return {
                ...crmKundenTopf,
                // eslint-disable-next-line no-constant-condition
                artCode: crmKundenTopf.artCode ?? 0,
            }
        });
    }


    async getVollmachten(request: IFetchFinnovaRequest): Promise<IPowerOfAttorney[]> {
        const {data} = await this.http.post<IPowerOfAttorney[]>(Endpoint, {
            bsaEndpoint: BSAEndpoint.VOLLMACHTEN,
            ...request,
        });

        const vollmachten = data.reduce((map, vollmacht) => {
            const key = `${vollmacht.vtId}:${vollmacht.vollmachtnehmerVertragszuordnung}`;
            if (!map.has(key)) {
                map.set(key, vollmacht);
            }

            return map;
        }, new Map<string, IPowerOfAttorney>());

        return Array.from(vollmachten.values());
    }

    async getEbankingVT(request: IFetchFinnovaRequest): Promise<IEbankingVT[]> {
        return this.http
            .post<IEbankingVT[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.EBANKING_VT,
                ...request,
            })
            .then(({data}) => data);
    }

    async getAccounts(request: IFetchFinnovaRequest): Promise<IAccount[]> {
        return this.http
            .post<IAccount[]>(Endpoint, {
                bsaEndpoint: BSAEndpoint.ACCOUNTS,
                ...request,
            })
            .then(({data}) => data);
    }
}
